import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import { RootState, Ticket, TicketStatus, TicketPriority, TicketCategory } from '../types';
import { fetchTickets, fetchTicket, clearSelectedTicket } from '../store/slices/ticketsSlice';
import { fetchDecodingAgentAnalysis, clearResults } from '../store/slices/decodingAgentSlice';
import { formatDate, isDatePast, isDateToday } from '../utils/dateUtils';
import { AlertData } from '../utils/getDecodingAgent';
import NewTicketForm from '../components/NewTicketForm';
import AnalysisModule from '../components/dashboard-components/AnalysisModule';
import { DataTable, Column } from '../components/common';
import { colorUtils } from '../theme';
import { getSeverityChipSx, getStatusChipSx, getStatusColor, getPriorityColor } from '../theme/mappers/ticketMappers';
import { useLayout } from '../components/contexts/LayoutContext';

const TicketsPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const location = useLocation();
  const { tickets, selectedTicket, isLoading: ticketsLoading, isDetailsLoading: detailsLoading, error: ticketsError } = useSelector((state: RootState) => state.tickets);
  const { result, isLoading: agentLoading, error: agentError } = useSelector((state: RootState) => state.decodingAgent);

  // Responsive breakpoints
  const isDetailsOpen = !!selectedTicket;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const isVerySmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { sidebarCollapsed } = useLayout();

  // Determine which columns to show based on available space
  const showAllColumns = !isDetailsOpen && !isSmallScreen && sidebarCollapsed;
  const showMediumColumns = !isDetailsOpen || !isVerySmallScreen;

  // Mock due dates generator based on SLA rules
  const generateDueDate = (createdAt: string, priority: TicketPriority): string => {
    const created = new Date(createdAt);
    const slaHours: Record<TicketPriority, number> = {
      [TicketPriority.CRITICAL]: 4,
      [TicketPriority.HIGH]: 24,
      [TicketPriority.MEDIUM]: 72,
      [TicketPriority.LOW]: 168,
    };

    const dueDate = new Date(created);
    dueDate.setHours(dueDate.getHours() + slaHours[priority]);
    return dueDate.toISOString();
  };

  // Mock category generator based on ticket title and description
  const generateCategory = (title: string, description: string): TicketCategory => {
    const titleLower = title.toLowerCase();
    const descLower = description.toLowerCase();

    if (titleLower.includes('malicious') || titleLower.includes('malware') ||
        titleLower.includes('ransomware') || titleLower.includes('encryption') ||
        descLower.includes('lockbit') || descLower.includes('malware')) {
      return TicketCategory.MALWARE;
    }

    if (titleLower.includes('phishing') || titleLower.includes('email') ||
        titleLower.includes('attachment') || titleLower.includes('proofpoint') ||
        descLower.includes('email security')) {
      return TicketCategory.PHISHING;
    }

    if (titleLower.includes('authentication') || titleLower.includes('credential') ||
        titleLower.includes('brute force') || titleLower.includes('failed login') ||
        descLower.includes('credential access')) {
      return TicketCategory.UNAUTHORIZED_ACCESS;
    }

    if (titleLower.includes('data breach') || titleLower.includes('exfiltration') ||
        titleLower.includes('data leak')) {
      return TicketCategory.DATA_BREACH;
    }

    if (titleLower.includes('dos') || titleLower.includes('ddos') ||
        titleLower.includes('denial of service')) {
      return TicketCategory.DENIAL_OF_SERVICE;
    }

    if (titleLower.includes('insider') || descLower.includes('insider threat')) {
      return TicketCategory.INSIDER_THREAT;
    }

    if (titleLower.includes('policy') || titleLower.includes('compliance') ||
        descLower.includes('policy violation')) {
      return TicketCategory.POLICY_VIOLATION;
    }

    if (titleLower.includes('c2') || titleLower.includes('command') ||
        titleLower.includes('lateral movement') || titleLower.includes('suspicious') ||
        titleLower.includes('tor') || descLower.includes('command and control')) {
      return TicketCategory.SUSPICIOUS_ACTIVITY;
    }

    return TicketCategory.OTHER;
  };

  // State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<TicketStatus | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<TicketPriority | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<TicketCategory | ''>('');
  const [hitlFilter, setHitlFilter] = useState(false);

  // State for new ticket dialog
  const [newTicketDialogOpen, setNewTicketDialogOpen] = useState(false);

  // Fetch tickets on component mount.
  // TODO(backend-filters): once the API supports filter params, pass the filter
  // state here instead of filtering client-side:
  //   const filters = { status: statusFilter || undefined, priority: priorityFilter || undefined, category: categoryFilter || undefined };
  //   dispatch(fetchTickets({ status: statusFilter || undefined, priority: priorityFilter || undefined, category: categoryFilter || undefined }) as any);
  // Then remove the matchesStatus/Priority/Category checks in filteredTickets below.
  useEffect(() => {
    dispatch(fetchTickets({}) as any);
  }, [dispatch]);

  useEffect(() => {
    const openTicketId = (location.state as any)?.openTicketId;
    if (openTicketId) {
      dispatch(fetchTicket(openTicketId) as any);
    }
  }, [location.state, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearSelectedTicket());
    };
  }, [dispatch]);

  // Handle search
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Handle refresh
  const handleRefresh = () => {
    dispatch(fetchTickets({}) as any);
  };

  // Handle row click
  const handleRowClick = (ticket: Ticket) => {
    dispatch(fetchTicket(ticket.id) as any);
  };

  // Handle new ticket dialog close
  const handleNewTicketDialogClose = () => {
    setNewTicketDialogOpen(false);
  };

  // Filter tickets client-side.
  // TODO(backend-filters): when backend filtering is ready, this block can be
  // simplified to just the searchTerm check — status/priority/category will
  // already be pre-filtered by the API.
  const filteredTickets = tickets
    .map((ticket) => {
      const mockCategory = (ticket.category === 'other' || !ticket.category)
        ? generateCategory(ticket.title, ticket.description)
        : ticket.category;

      return {
        ...ticket,
        due_date: ticket.due_date || generateDueDate(ticket.created_at, ticket.priority),
        category: mockCategory,
      };
    })
    .filter((ticket) => {
      const matchesSearch = !searchTerm || (
        ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.priority.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesStatus = !statusFilter || ticket.status === statusFilter;
      const matchesPriority = !priorityFilter || ticket.priority === priorityFilter;
      const matchesCategory = !categoryFilter || ticket.category === categoryFilter;
      const matchesHitl = !hitlFilter || ticket.requires_human_review === true;

      return matchesSearch && matchesStatus && matchesPriority && matchesCategory && matchesHitl;
    });


  // Get due date color
  const getDueDateColor = (dueDate: string | null) => {
    if (!dueDate) return 'default';
    if (isDatePast(dueDate)) return 'error';
    if (isDateToday(dueDate)) return 'warning';
    return 'default';
  };

  const getAgent = (alertData: AlertData) => {
    dispatch(fetchDecodingAgentAnalysis(alertData) as any);
  };

  const handleClearResults = (): void => {
    dispatch(clearResults());
  };

  // Define table columns with priority levels
  // Priority: 1 = always show, 2 = show when medium space, 3 = show only with full space
  const allColumns: (Column<Ticket> & { priority: number })[] = [
    {
      key: 'id',
      header: 'Ticket ID',
      minWidth: 80,
      align: 'left',
      priority: 1,
      render: (ticket) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              whiteSpace: 'nowrap',
            }}
          >
            {ticket.id.split('-')[0]}
          </Typography>
          {ticket.source === 'auto' && (
            <Chip
              label="AUTO"
              size="small"
              sx={{
                height: 16,
                fontSize: '0.58rem',
                fontWeight: 700,
                backgroundColor: `${theme.palette.primary.main}22`,
                color: theme.palette.primary.main,
                border: `1px solid ${theme.palette.primary.main}55`,
                letterSpacing: '0.03em',
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )}
        </Box>
      ),
    },
    {
      key: 'title',
      header: 'Title',
      minWidth: 140,
      maxWidth: 240,
      align: 'left',
      priority: 1,
      render: (ticket) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          {ticket.requires_human_review && (
            <Tooltip title={ticket.hitl_reason ?? 'Flagged for human review'} placement="top">
              <FlagIcon sx={{ fontSize: 13, color: theme.palette.warning.main, flexShrink: 0 }} />
            </Tooltip>
          )}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {ticket.title}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'client_name',
      header: 'Client',
      minWidth: 120,
      maxWidth: 180,
      align: 'left',
      priority: 1,
      render: (ticket) => (
        ticket.client_name ? (
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {ticket.client_name}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: theme.palette.text.disabled, whiteSpace: 'nowrap' }}>
            —
          </Typography>
        )
      ),
    },
    {
      key: 'status',
      header: 'Status',
      minWidth: 110,
      align: 'center',
      priority: 1,
      render: (ticket) => (
        <Chip
          label={ticket.status.replace('_', ' ')}
          size="small"
          sx={getStatusChipSx(getStatusColor(ticket.status, theme))}
        />
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      minWidth: 90,
      align: 'center',
      priority: 1,
      render: (ticket) => (
          <Chip
            label={ticket.priority}
            size="small"
            sx={getSeverityChipSx(getPriorityColor(ticket.priority, theme))}
          />
      ),
    },
    {
      key: 'category',
      header: 'Category',
      minWidth: 130,
      noWrap: true,
      align: 'left',
      priority: 2,
      accessor: (ticket) => ticket.category.replace('_', ' '),
    },
    {
      key: 'assignee',
      header: 'Assignee',
      minWidth: 130,
      noWrap: true,
      align: 'left',
      priority: 2,
      render: (ticket) => (
        ticket.assignee ? (
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
            {`${ticket.assignee.first_name} ${ticket.assignee.last_name}`}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: theme.palette.text.disabled, whiteSpace: 'nowrap' }}>
            Unassigned
          </Typography>
        )
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      minWidth: 100,
      noWrap: true,
      align: 'left',
      priority: 3,
      render: (ticket) => (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.85rem',
            whiteSpace: 'nowrap',
          }}
        >
          {formatDate(ticket.created_at)}
        </Typography>
      ),
    },
    {
      key: 'due_date',
      header: 'Due Date',
      minWidth: 110,
      align: 'left',
      priority: 3,
      render: (ticket) => (
        ticket.due_date ? (
          <Chip
            label={formatDate(ticket.due_date)}
            color={getDueDateColor(ticket.due_date) as any}
            size="small"
            variant="outlined"
            sx={{
              borderRadius: 1,
              fontWeight: 500,
            }}
          />
        ) : (
          <Typography variant="body2" sx={{ color: theme.palette.text.disabled, whiteSpace: 'nowrap' }}>
            No due date
          </Typography>
        )
      ),
    },
  ];

  // Filter columns based on available space
  const columns: Column<Ticket>[] = useMemo(() => {
    return allColumns.filter(col => {
      if (col.priority === 1) return true;
      if (col.priority === 2) return showMediumColumns;
      if (col.priority === 3) return showAllColumns;
      return true;
    });
  }, [showAllColumns, showMediumColumns]);

  return (
    <Box sx={{
      backgroundColor: theme.palette.background.default,
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      px: { xs: 2, sm: 3, md: 4 },
      pt: 10, pb: 3,
    }}>
      <Box sx={{
        width: '100%',
        maxWidth: '100%',
      }}>
        {/* Header Section */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 2, sm: 0 },
          mb: 4,
        }}>
          <Box>
            <Typography variant="h1" sx={{ color: theme.palette.text.primary, fontWeight: 600, marginBottom: 0.5 }}>
              Tickets
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
              }}
            >
              Manage and track your security tickets
            </Typography>
          </Box>
        </Box>

        {/* Filters Section */}
        <Paper sx={{
          mb: 3,
          pr: 2,
          backgroundColor: 'transparent',
          border: 'none',
          boxShadow: 'none',
        }}>
          <Grid container spacing={3} alignItems='flex-end'>

            {/* Search Area */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.tertiary,
                    fontWeight: 700,
                    display: 'block',
                    mb: 1,
                    ml: 0.5,
                    letterSpacing: '0.05em'
                  }}
                >
                  Search
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Search tickets..."
                  value={searchTerm}
                  onChange={handleSearch}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: theme.palette.text.tertiary, fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.palette.background.tertiary,
                      height: 40,
                      '& fieldset': { border: 'none' },
                      '&:hover': { backgroundColor: theme.palette.background.secondary },
                      '&.Mui-focused': {
                        backgroundColor: theme.palette.background.default,
                        boxShadow: `0 0 0 1px ${theme.palette.text.primary}`,
                      }
                    }
                  }}
                />
              </Box>
            </Grid>

            {/* Dropdowns Area */}
            <Grid size={{ xs: 12, md: 7 }}>
              <Box sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'flex-end',
                justifyContent: 'flex-end',
              }}>
                {[
                  { label: 'Status', value: statusFilter, setter: setStatusFilter, options: TicketStatus },
                  { label: 'Priority', value: priorityFilter, setter: setPriorityFilter, options: TicketPriority },
                  { label: 'Category', value: categoryFilter, setter: setCategoryFilter, options: TicketCategory },
                ].map((filter) => (
                  <Box key={filter.label} sx={{ minWidth: 140 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.tertiary,
                        fontWeight: 700,
                        display: 'block',
                        mb: 1,
                        ml: 0.5,
                        letterSpacing: '0.05em'
                      }}
                    >
                      {filter.label}
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={filter.value}
                        onChange={(e) => filter.setter(e.target.value as any)}
                        displayEmpty
                        sx={{
                          height: 40,
                          fontSize: '0.8rem',
                          backgroundColor: theme.palette.background.tertiary,
                          color: theme.palette.text.primary,
                          fontWeight: 500,
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                          '&:hover': { backgroundColor: theme.palette.background.secondary },
                          '&.Mui-focused': {
                            backgroundColor: theme.palette.background.default,
                            boxShadow: `0 0 0 1px ${theme.palette.text.primary}`
                          }
                        }}
                      >
                        <MenuItem value="" sx={{ fontSize: '0.8rem', color: theme.palette.text.tertiary }}>
                          All
                        </MenuItem>
                        {Object.values(filter.options).map((opt: any) => (
                          <MenuItem key={opt} value={opt} sx={{ fontSize: '0.8rem' }}>
                            {opt.charAt(0).toUpperCase() + opt.slice(1).replace('_', ' ')}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ))}

                <Box sx={{ mb: 0.25, display: 'flex', gap: 1 }}>
                  <Chip
                    icon={<FlagIcon sx={{ fontSize: '14px !important' }} />}
                    label="Needs Review"
                    size="small"
                    onClick={() => setHitlFilter(v => !v)}
                    sx={{
                      height: 40,
                      borderRadius: 1,
                      px: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      backgroundColor: hitlFilter
                        ? colorUtils.withOpacity(theme.palette.warning.main, 0.15)
                        : theme.palette.background.tertiary,
                      color: hitlFilter ? theme.palette.warning.main : theme.palette.text.secondary,
                      border: `1px solid ${hitlFilter
                        ? colorUtils.withOpacity(theme.palette.warning.main, 0.4)
                        : 'transparent'}`,
                      '&:hover': {
                        backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.12),
                        color: theme.palette.warning.main,
                      },
                    }}
                  />
                  <IconButton
                    onClick={handleRefresh}
                    sx={{
                      bgcolor: theme.palette.background.tertiary,
                      color: theme.palette.info.main,
                      borderRadius: 1,
                      width: 40,
                      height: 40,
                      '&:hover': {
                        bgcolor: theme.palette.info.main,
                        color: theme.palette.background.tertiary,
                        transform: 'rotate(180deg)',
                      },
                      transition: 'all 0.4s ease',
                    }}
                  >
                    <RefreshIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Data Table */}
        <DataTable
          columns={columns}
          data={filteredTickets}
          rowKey={(ticket) => ticket.id}
          isLoading={ticketsLoading}
          error={ticketsError}
          emptyMessage="No tickets found"
          emptySubMessage={searchTerm ? 'Try adjusting your search terms' : 'Create your first ticket to get started'}
          loadingMessage="Loading tickets..."
          onRowClick={handleRowClick}
          onRetry={handleRefresh}
          minTableWidth={400}
          activeRowKey={selectedTicket?.id ?? null}
          rowHeight={52}
        />

        {/* New Ticket Dialog */}
        <Dialog
          open={newTicketDialogOpen}
          onClose={handleNewTicketDialogClose}
          maxWidth="md"
          fullWidth
          disableScrollLock
          PaperProps={{
            sx: {
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.border.main}`,
              borderRadius: 2,
              boxShadow: '0 24px 48px rgba(0, 0, 0, 0.4)',
            },
          }}
        >
          <DialogTitle sx={{
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${theme.palette.border.light}`,
            fontWeight: 700,
          }}>
            Create New Ticket
          </DialogTitle>
          <DialogContent>
            <NewTicketForm onSuccess={handleNewTicketDialogClose} />
          </DialogContent>
          <DialogActions sx={{
            borderTop: `1px solid ${theme.palette.border.light}`,
            p: 2,
          }}>
            <Button
              onClick={handleNewTicketDialogClose}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.interactive.hover,
                },
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Decoding Agent Results Dialog */}
        <AnalysisModule
          open={!!result || !!agentError || agentLoading}
          onClose={handleClearResults}
          agentLoading={agentLoading}
          agentError={agentError}
          result={result}
        />
      </Box>
    </Box>
  );
};

export default TicketsPage;
